import { create } from "zustand";

import { ApiError, api } from "../lib/api";

// Sill Plus design spec §4.1 (docs/superpowers/specs/2026-05-12-sill-plus-design.md):
// `useEntitlements` is a module-level cache shared across every consumer that
// gates UI on Plus. Following ADR-027 (shared store + enumerated refresh
// policy) so navigation between Dashboard, Settings, Calculators, and the
// upgrade sheet never refetches and never flickers — the hot path is every
// app boot, and the result rarely changes during a session.
//
// Refresh is allowed only on enumerated surfaces (mount, billing-tab manual
// refresh, post-webhook subscription state transition). Page navigation does
// NOT refresh; that's the whole point.

// EntitlementStatus mirrors the backend JSON (services/entitlements:
// `EntitlementStatus{Granted, HeldBy}` with `omitempty` on held_by).
export type EntitlementStatus = {
  granted: boolean;
  // Empty string when not granted. `omitempty` from the backend means the key
  // may be absent on the wire; treat undefined and "" identically.
  held_by?: "self" | "household_member" | "";
};

export type ProfileEntitlements = {
  // Feature slug → status. The backend currently emits exactly `sill_plus` per
  // `entitlements.ValidFeatures`; modeling it as a map keeps room for future
  // slugs without a hook API change.
  [feature: string]: EntitlementStatus;
};

// Canonical slug for the Sill Plus umbrella feature. Mirrors the backend
// constant in `internal/services/entitlements/service.go` (FeaturePlus).
export const FEATURE_PLUS = "sill_plus";

type EntitlementsStore = {
  // The latest snapshot. Null when no fetch has completed yet — distinguishes
  // "loading first time" from "the server said no features".
  data: ProfileEntitlements | null;
  error: Error | null;
  isFetching: boolean;
  hasLoaded: boolean;

  ensureLoaded: (
    getToken: () => Promise<string | null | undefined>,
  ) => Promise<void>;
  refresh: (
    getToken: () => Promise<string | null | undefined>,
  ) => Promise<void>;
  reset: () => void;
};

// Module-scoped in-flight tracker. Lives outside the store so subscribers
// don't re-render purely on promise tracking — mirrors `stores/readiness.ts`.
let inFlight: Promise<void> | null = null;

type EntitlementsResponse = { entitlements: ProfileEntitlements };

// Refresh runs on enumerated surfaces and the backend re-resolves Clerk
// Billing every call, so the same Plus state is re-serialized to a fresh
// object on every successful fetch. Returning the prior `data` reference
// when nothing changed keeps zustand from notifying every Plus-gated
// component on each refresh — same intent as the same-reference discipline
// the readiness store relies on.
function entitlementsEqual(
  a: ProfileEntitlements | null,
  b: ProfileEntitlements,
): boolean {
  if (a === null) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    const av = a[k];
    const bv = b[k];
    if (!bv) return false;
    if (av.granted !== bv.granted || (av.held_by ?? "") !== (bv.held_by ?? "")) {
      return false;
    }
  }
  return true;
}

async function runFetch(
  getToken: () => Promise<string | null | undefined>,
  set: (
    partial:
      | Partial<EntitlementsStore>
      | ((state: EntitlementsStore) => Partial<EntitlementsStore>),
  ) => void,
  get: () => EntitlementsStore,
): Promise<void> {
  set({ isFetching: true });
  try {
    const token = (await getToken()) ?? undefined;
    const response = await api.get<EntitlementsResponse>(
      "/api/profile/entitlements",
      token,
    );
    const incoming = response.entitlements ?? {};
    const prev = get().data;
    set({
      data: entitlementsEqual(prev, incoming) ? (prev as ProfileEntitlements) : incoming,
      error: null,
      hasLoaded: true,
      isFetching: false,
    });
  } catch (e) {
    // The endpoint is registered only when the entitlements service is wired
    // (per ADR-023). When unwired it returns 404 — treat that as "no gated
    // features held" so dev/preview envs without Clerk Billing don't trip an
    // error banner. Same default for the "no profile yet" branch (handler
    // returns `{entitlements: {}}` which already flows through the success
    // path).
    if (e instanceof ApiError && e.status === 404) {
      set({
        data: {},
        error: null,
        hasLoaded: true,
        isFetching: false,
      });
      return;
    }
    // Keep `data` and `hasLoaded` so a transient error doesn't blank a
    // previously-rendered Plus state.
    set({ error: e as Error, isFetching: false });
  }
}

export const useEntitlementsStore = create<EntitlementsStore>((set, get) => ({
  data: null,
  error: null,
  isFetching: false,
  hasLoaded: false,

  ensureLoaded: async (getToken) => {
    const state = get();
    if (state.hasLoaded) return;
    if (inFlight) return inFlight;
    inFlight = runFetch(getToken, set, get).finally(() => {
      inFlight = null;
    });
    return inFlight;
  },

  refresh: async (getToken) => {
    if (inFlight) return inFlight;
    inFlight = runFetch(getToken, set, get).finally(() => {
      inFlight = null;
    });
    return inFlight;
  },

  reset: () => {
    inFlight = null;
    set({
      data: null,
      error: null,
      hasLoaded: false,
      isFetching: false,
    });
  },
}));
